export interface PreparationPDFConfig {
  pdfUrl: string;
  title: string;
}

export class PreparationPDFFactory {
  private static readonly PDF_URLS = {
    RESONANCIA_CON_CONTRASTE: 'https://storage.googleapis.com/preparaciones/rnm_con_contraste.pdf',
    RESONANCIA_SIN_CONTRASTE: 'https://storage.googleapis.com/preparaciones/rnm_sin_contraste.pdf',
    SCANNER_CON_CONTRASTE: 'https://storage.googleapis.com/preparaciones/scanner_con_contraste.pdf',
    SCANNER_SIN_CONTRASTE: 'https://storage.googleapis.com/preparaciones/scanner_sin_contraste.pdf',
    MAMOGRAFIA: 'https://storage.googleapis.com/preparaciones/mamografia.pdf',
    ECOTOMOGRAFIA_MAMARIA: 'https://storage.googleapis.com/preparaciones/ecotomografia_mamaria.pdf'
  };

  private static readonly SPECIALTY_IDS = {
    SCANNER: 26,
    RESONANCIA: 40,
    MAMOGRAFIA: 11,
    ECOTOMOGRAFIA: 2
  };

  /**
   * Determina el PDF correcto basado en el nombre de la prestación y el ID de especialidad
   * @param prestacionName - Nombre de la prestación
   * @param specialtyId - ID de la especialidad
   * @returns Configuración del PDF o null si no aplica
   */
  static getPreparationPDF(prestacionName: string, specialtyId: number): PreparationPDFConfig | null {
    const name = prestacionName.toUpperCase();

    // Mamografía: mostrar siempre cuando el ID sea 11
    if (specialtyId === this.SPECIALTY_IDS.MAMOGRAFIA) {
      return {
        pdfUrl: this.PDF_URLS.MAMOGRAFIA,
        title: 'Mamografía'
      };
    }

    // Ecotomografía: mostrar solo si contiene "ECOGRAFIA MAMARIA" (independientemente del specialtyId)
    if (name.includes('ECOGRAFIA MAMARIA')) {
      return {
        pdfUrl: this.PDF_URLS.ECOTOMOGRAFIA_MAMARIA,
        title: 'Ecotomografía Mamaria'
      };
    }

    // Verificar si la especialidad es Scanner o Resonancia
    if (specialtyId !== this.SPECIALTY_IDS.SCANNER && specialtyId !== this.SPECIALTY_IDS.RESONANCIA) {
      return null;
    }

    // Detectar si es con contraste
    const hasContrast = this.hasContrast(name);

    if (specialtyId === this.SPECIALTY_IDS.SCANNER) {
      return {
        pdfUrl: hasContrast ? this.PDF_URLS.SCANNER_CON_CONTRASTE : this.PDF_URLS.SCANNER_SIN_CONTRASTE,
        title: hasContrast ? 'Tomografía Computada (TC) con contraste' : 'Tomografía Computada (TC) sin contraste'
      };
    }

    if (specialtyId === this.SPECIALTY_IDS.RESONANCIA) {
      return {
        pdfUrl: hasContrast ? this.PDF_URLS.RESONANCIA_CON_CONTRASTE : this.PDF_URLS.RESONANCIA_SIN_CONTRASTE,
        title: hasContrast ? 'Resonancia Magnética (RM) con contraste' : 'Resonancia Magnética (RM) sin contraste'
      };
    }

    return null;
  }

  /**
   * Detecta si la prestación es con contraste basándose en el nombre
   * @param prestacionName - Nombre de la prestación en mayúsculas
   * @returns true si es con contraste, false si es sin contraste
   */
  private static hasContrast(prestacionName: string): boolean {
    // Palabras clave que indican contraste
    const contrastKeywords = [
      'CON CONTRASTE',
      'ANGIOTAC',
      'UROTAC'
    ];

    // Palabras clave que indican sin contraste
    const noContrastKeywords = [
      'SIN CONTRASTE',
      'PIELOTAC',
    ];

    // Verificar primero si explícitamente dice "sin contraste"
    if (noContrastKeywords.some(keyword => prestacionName.includes(keyword))) {
      return false;
    }

    // Verificar si contiene palabras clave de contraste
    return contrastKeywords.some(keyword => prestacionName.includes(keyword));
  }

  /**
   * Verifica si una especialidad requiere modal de preparaciones
   * @param specialtyId - ID de la especialidad
   * @returns true si requiere modal, false en caso contrario
   */
  static requiresPreparationModal(specialtyId: number): boolean {
    return specialtyId === this.SPECIALTY_IDS.SCANNER || 
           specialtyId === this.SPECIALTY_IDS.RESONANCIA ||
           specialtyId === this.SPECIALTY_IDS.MAMOGRAFIA;
  }
}
